import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowRight, Plus } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { Button, buttonVariants } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { cn } from '../../lib/cn'
import { needsReauthentication } from '../../lib/errors'
import { EmptyState, ErrorState, Loading, PageHeading, Problem } from '../components/chrome'
import { useReauth } from '../components/reauth'
import { useI18n } from '../lib/i18n'
import { forgetTouch, passkeyErrorMessage } from '../lib/passkeys'
import { useOverview, type ActivityEntry, type NoaNotice, type Overview } from '../lib/screens'
import { useStaffState } from '../lib/session'
import { STAFF_STATE_KEY } from '../lib/trpc'
import { useRegisterPasskey } from '../lib/use-passkeys'

/** ADR 0008 §1: at most 3 passkeys per person. */
const MAX_PASSKEYS = 3

/*
 * «Inicio» of the panel (PRD 04 §2.1, ADR 0008 §3): the counts (active companies, active people
 * of the active companies, published courses and, with ADR 0009 §8, the assignments in progress
 * and the global completion rate of the active companies' people), the way to «Empresas» and
 * «Cursos», the recent activity of Eklevo's staff (ADR 0009 §8: who did what to which company or
 * course of the catalogue, never a person of a company), and the person's own access: when the
 * session ends, and adding a second or third passkey (the recommended two: the computer's and the
 * phone's), a sensitive action that asks for the passkey first. Without active companies, the way
 * to the company wizard (PRD 04 §2.1's call to add one, ADR 0009 §7). The «Noa» indicator (ADR
 * 0010 §8 and §9): the companies past 80 % of their monthly cap of Noa, or at it, and the ones
 * where Noa is paused for an update; the band only, never the amount. Each view leaves
 * `backoffice.viewed` in Eklevo's audit trail. Not in the Figma (86:2451 is «Empresas»): marked
 * for design review.
 */
export function HomePage() {
  const { t } = useI18n()
  const state = useStaffState()
  const overview = useOverview()
  if (state.data?.status !== 'signed-in') return null
  const { endsAt, passkeys } = state.data
  return (
    <div className="space-y-8">
      <PageHeading title={t('home.title')}>{t('home.body')}</PageHeading>
      {overview.isPending ? (
        <Loading />
      ) : overview.isError ? (
        <ErrorState onRetry={() => void overview.refetch()} />
      ) : (
        <Indicators overview={overview.data} />
      )}
      {overview.isSuccess && <NoaNotices notices={overview.data.noa} />}
      <Shortcuts />
      {overview.isSuccess && <Activity entries={overview.data.activity} />}
      <AccessSection endsAt={endsAt} passkeys={passkeys} />
    </div>
  )
}

function Indicators({ overview }: { overview: Overview }) {
  const { t, locale } = useI18n()
  const id = useId()
  const number = new Intl.NumberFormat(locale)
  const assignments = overview.assignmentsInProgress + overview.assignmentsCompleted
  // Never rounded up: 99.6 % is not all of them.
  const rate = assignments
    ? new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(
        Math.floor((100 * overview.assignmentsCompleted) / assignments) / 100,
      )
    : t('home.stats.noValue')
  const stats = [
    { label: t('home.stats.organizations'), value: number.format(overview.activeOrganizations) },
    {
      label: t('home.stats.people'),
      value: number.format(overview.activePeople),
      hint: t('home.stats.peopleHint'),
    },
    { label: t('home.stats.courses'), value: number.format(overview.publishedCourses) },
    {
      label: t('home.stats.assignments'),
      value: number.format(overview.assignmentsInProgress),
      hint: t('home.stats.assignmentsHint'),
    },
    {
      label: t('home.stats.completion'),
      value: rate,
      hint: assignments
        ? t('home.stats.completionHint', {
            count: overview.assignmentsCompleted,
            total: assignments,
          })
        : t('home.stats.completionNone'),
    },
  ]
  return (
    <section aria-labelledby={id} className="space-y-4">
      <h2 id={id} className="sr-only">
        {t('home.stats.label')}
      </h2>
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {stats.map((s) => (
          <Card key={s.label} className="flex flex-col">
            <dt className="text-sm text-ink-muted">{s.label}</dt>
            <dd className="order-first text-3xl font-medium tabular-nums">{s.value}</dd>
            {s.hint && <dd className="mt-1 text-xs text-ink-muted">{s.hint}</dd>}
          </Card>
        ))}
      </dl>
      {overview.activeOrganizations === 0 && (
        <EmptyState title={t('home.empty.title')}>
          <p>{t('home.empty.body')}</p>
          <Link to="/empresas/nueva" className={cn(buttonVariants(), 'mt-4')}>
            <Plus aria-hidden className="size-4" />
            {t('home.empty.add')}
          </Link>
        </EmptyState>
      )}
    </section>
  )
}

/**
 * «Noa» (ADR 0010 §8 and §9): each company that needs a look, by name, with the way to its detail,
 * and why: past 80 % of its monthly cap, at it (paused until next month), or paused because Noa
 * runs with other versions than the ones it was turned on with. Never an amount.
 */
function NoaNotices({ notices }: { notices: NoaNotice[] }) {
  const { t } = useI18n()
  const id = useId()
  return (
    <section aria-labelledby={id} className="space-y-4">
      <div>
        <h2 id={id} className="text-xl">
          {t('home.noa.title')}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-ink-muted">{t('home.noa.body')}</p>
      </div>
      {notices.length === 0 ? (
        <p className="rounded-card border border-line bg-surface px-5 py-4 text-sm text-ink-muted">
          {t('home.noa.empty')}
        </p>
      ) : (
        <ul className="divide-y divide-line rounded-card border border-line bg-surface">
          {notices.map((n) => (
            <li key={n.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3">
              <Link
                to="/empresas/$organizationId"
                params={{ organizationId: n.id }}
                aria-label={t('companies.openLabel', { company: n.name })}
                className="min-w-0 font-medium underline-offset-2 [overflow-wrap:anywhere] hover:underline"
              >
                {n.name}
              </Link>
              <span className="text-sm text-ink-muted">
                {[
                  ...(n.updating ? [t('home.noa.updating')] : []),
                  ...(n.band ? [t(`home.noa.${n.band}`)] : []),
                ].join(' ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function Shortcuts() {
  const { t } = useI18n()
  const id = useId()
  const links = [
    { to: '/empresas', title: t('nav.companies'), body: t('home.go.companies') },
    { to: '/cursos', title: t('nav.catalog'), body: t('home.go.catalog') },
  ] as const
  return (
    <section aria-labelledby={id}>
      <h2 id={id} className="sr-only">
        {t('home.go.label')}
      </h2>
      <ul className="grid gap-4 md:grid-cols-2">
        {links.map((l) => (
          <li key={l.to}>
            <Link
              to={l.to}
              className="flex h-full items-center gap-4 rounded-card border border-line bg-surface p-5 hover:bg-black/[0.02] sm:p-6"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-lg font-medium">{l.title}</span>
                <span className="mt-1 block text-sm text-ink-muted">{l.body}</span>
              </span>
              <ArrowRight aria-hidden className="size-5 shrink-0 text-ink-muted" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * The recent activity of Eklevo's staff (ADR 0009 §8): the newest actions first, each with who did
 * it (their name in the list of operators, or someone of the team when the list has none), what,
 * to which company or course of the catalogue, and when. Never a person of a company.
 */
function Activity({ entries }: { entries: ActivityEntry[] }) {
  const { t } = useI18n()
  const id = useId()
  return (
    <section aria-labelledby={id} className="space-y-4">
      <div>
        <h2 id={id} className="text-xl">
          {t('home.activity.title')}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-ink-muted">{t('home.activity.body')}</p>
      </div>
      {entries.length === 0 ? (
        <p className="rounded-card border border-line bg-surface px-5 py-4 text-sm text-ink-muted">
          {t('home.activity.empty')}
        </p>
      ) : (
        <ol className="divide-y divide-line rounded-card border border-line bg-surface">
          {entries.map((e, i) => (
            <li key={`${e.at}-${i}`} className="px-5 py-3">
              <p className="[overflow-wrap:anywhere]">
                <span className="font-medium">{e.operator ?? t('home.activity.someone')}</span>{' '}
                <span>
                  {t(`home.activity.${e.action}`, {
                    company: e.organization?.name ?? '',
                    course: e.course
                      ? (e.course.title ?? t('home.activity.untitledCourse'))
                      : t('home.activity.goneCourse'),
                  })}
                </span>
              </p>
              <p className="mt-0.5 text-xs text-ink-muted">
                <time dateTime={e.at}>{t('home.activity.at', { at: new Date(e.at) })}</time>
              </p>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

function AccessSection({ endsAt, passkeys }: { endsAt: string; passkeys: number }) {
  const { t } = useI18n()
  const id = useId()
  return (
    <section aria-labelledby={id} className="space-y-4">
      <h2 id={id} className="text-xl">
        {t('home.access.title')}
      </h2>
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <h3 className="text-lg font-medium">{t('home.session.title')}</h3>
          <p className="mt-2 text-sm text-ink-muted">
            {t('home.session.endsAt', { endsAt: new Date(endsAt) })}
          </p>
        </Card>
        <Passkeys count={passkeys} />
      </div>
    </section>
  )
}

function Passkeys({ count }: { count: number }) {
  const { t } = useI18n()
  const reauth = useReauth()
  const queryClient = useQueryClient()
  const register = useRegisterPasskey()
  const [creating, setCreating] = useState(false)
  const [added, setAdded] = useState<number | null>(null)
  const createButton = useRef<HTMLButtonElement>(null)

  // The new passkey is created by a press of its own: Safari only asks the device after one.
  useEffect(() => {
    if (creating) createButton.current?.focus()
  }, [creating])

  const start = async () => {
    setAdded(null)
    register.reset()
    if (await reauth.confirm()) setCreating(true)
  }
  const create = () =>
    register.mutate(undefined, {
      onSuccess: (r) => {
        setCreating(false)
        setAdded(r.passkeys)
        void queryClient.invalidateQueries({ queryKey: STAFF_STATE_KEY })
      },
      onError: (err) => {
        // More than 5 minutes since the passkey was touched: ask for it again.
        if (!needsReauthentication(err)) return
        forgetTouch()
        setCreating(false)
        void start()
      },
    })

  const failed =
    register.isError && !needsReauthentication(register.error)
      ? t(passkeyErrorMessage(register.error))
      : null
  return (
    <Card>
      <h3 className="text-lg font-medium">{t('home.passkeys.title')}</h3>
      <p className="mt-2 text-sm">{t('home.passkeys.count', { count })}</p>
      <p className="mt-2 text-sm text-ink-muted">{t('home.passkeys.hint')}</p>
      <p role="status" className="text-sm text-success not-empty:mt-4">
        {added !== null ? t('home.passkeys.added', { count: added }) : ''}
      </p>
      <Problem>{failed}</Problem>
      {count >= MAX_PASSKEYS ? (
        <p className="mt-4 text-sm text-ink-muted">{t('home.passkeys.full')}</p>
      ) : creating ? (
        <div className="mt-4 rounded-lg border border-line p-4">
          <h4 className="font-medium">{t('home.passkeys.createTitle')}</h4>
          <p className="mt-1 text-sm text-ink-muted">{t('home.passkeys.createBody')}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button ref={createButton} disabled={register.isPending} onClick={create}>
              {register.isPending ? t('enroll.busy') : t('home.passkeys.create')}
            </Button>
            <Button variant="secondary" onClick={() => setCreating(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="secondary" className="mt-4" onClick={() => void start()}>
          {t('home.passkeys.add')}
        </Button>
      )}
    </Card>
  )
}
